// Single source of truth for editable storefront content (About, Contact,
// Team, Testimonials, Footer). Stored in `settings/siteContent` — which is
// publicly readable per firestore.rules — and edited from the admin
// "Site Content" page. Components fall back to DEFAULT_CONTENT until/if the
// admin customises a field.

import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  defaultServices, defaultPartners, defaultAudiences, defaultTrustBadges,
} from '../data/services';

export const DEFAULT_CONTENT = {
  announcement: {
    enabled: true,
    text: '⚡ ICT & Electrical Services across Kenya  ·  Authorised Dealer & Partner  ·  Call +254 141 988977',
  },
  footer: {
    tagline:
      'A leading ICT and Electrical Services company providing innovative, reliable and cost-effective solutions to businesses, institutions and individuals across Kenya.',
    email: 'follextech@gmail.com',
    phone: '+254 141 988977',
    location: 'Nairobi, Kenya',
  },
  social: {
    instagram: 'https://instagram.com',
    facebook: 'https://facebook.com',
    twitter: 'https://twitter.com',
    tiktok: 'https://tiktok.com',
    pinterest: 'https://pinterest.com',
  },
  contact: {
    address: 'Nairobi, Kenya',
    phone: '+254 141 988977',
    phoneHours: 'Mon-Fri: 8AM-6PM EAT',
    email: 'follextech@gmail.com',
    email2: '',
    hours: 'Mon-Sat: 8AM-7PM · Sun: Closed (emergency support available)',
  },
  about: {
    heroTitle: 'Technology Solutions for a Smarter Future',
    heroSubtitle:
      'Follex Tech is a leading ICT and Electrical Services company serving businesses, institutions and individuals across Kenya',
    stats: [
      { number: '500+', label: 'Projects Delivered' },
      { number: '7', label: 'Service Lines' },
      { number: '47', label: 'Counties Served' },
      { number: '24/7', label: 'Support' },
    ],
    missionTitle: 'Our Mission',
    missionLead:
      'To deliver innovative, reliable and cost-effective ICT and electrical solutions that empower organisations and individuals across Kenya.',
    missionBody:
      'From IT support, networking and security systems to software development and electrical works, Follex Tech is your single trusted partner. As an authorised dealer for leading brands, we also supply genuine hardware — backing everything with skilled technicians, fast response and quality you can trust.',
    storeImage: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=600',
  },
  team: [
    { name: 'Alex Mwangi', role: 'Founder & CEO', image: 'https://i.pravatar.cc/150?img=12' },
    { name: 'Sarah Otieno', role: 'Head of Operations', image: 'https://i.pravatar.cc/150?img=5' },
    { name: 'Michael Chen', role: 'Lead Technician', image: 'https://i.pravatar.cc/150?img=3' },
    { name: 'Emma Wanjiru', role: 'Customer Success', image: 'https://i.pravatar.cc/150?img=9' },
  ],
  testimonials: [
    { quote: 'Follex Tech wired and networked our entire office — structured cabling, CCTV and a MikroTik setup. Professional from start to finish.', name: 'Brian Kamau', role: 'Operations Manager', avatar: 'https://i.pravatar.cc/150?img=15', emoji: '🖧' },
    { quote: 'They handled our school’s computer lab installation and ongoing IT support. Fast response whenever we need them.', name: 'Aisha Hassan', role: 'ICT Coordinator', avatar: 'https://i.pravatar.cc/150?img=20', emoji: '💻' },
    { quote: 'Bought genuine laptops with real warranty and got the electrical fit-out done by the same team. Quality you can trust.', name: 'David Ouma', role: 'SME Owner', avatar: 'https://i.pravatar.cc/150?img=33', emoji: '⚡' },
  ],
  // ── Services & company content (admin-editable) ──────────────────────────
  services: defaultServices,
  partners: defaultPartners,
  audiences: defaultAudiences,
  trustBadges: defaultTrustBadges,
};

// Shallow-per-section merge: a stored section replaces only the keys it defines;
// arrays (team, testimonials, stats) are replaced wholesale when present.
export function mergeContent(stored) {
  if (!stored) return DEFAULT_CONTENT;
  const out = { ...DEFAULT_CONTENT };
  for (const key of Object.keys(DEFAULT_CONTENT)) {
    const def = DEFAULT_CONTENT[key];
    const val = stored[key];
    if (val == null) continue;
    out[key] = Array.isArray(def) ? val : { ...def, ...val };
  }
  return out;
}

export function useSiteContent() {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'siteContent'));
        if (alive && snap.exists()) setContent(mergeContent(snap.data()));
      } catch (e) {
        console.error('Site content load failed:', e);
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  return { content, loaded };
}
